import CardProviderLabel from '@/components/card-provider-label';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { ICard, ICardFillJob, IPhone } from '@/entities/client.entity';
import { requestCardBalance, requestCardEnrich, requestPhoneCardFill } from '@/lib/card-fill-actions';
import { resolveCardPhoneLabel } from '@/lib/card-phone-label';
import {
	CARD_FILL_JOB_STATUS_LABELS,
	CARD_FILL_JOB_TYPE_LABELS,
	filterJobsByCardId,
	filterJobsByPhoneId,
	formatCardSources,
	formatCardBalanceMeta,
	formatDateSource,
	formatDateTime,
	formatDuration,
	getCardJobStatusLabel,
	getLatestBalanceFetchJob,
	getLatestPhoneLookupJob,
	hasActiveBalanceFetchJob,
	hasActiveCardJob,
	isCardBalanceFetchReady,
	isJobActive,
	isLatestCardJobFailed,
	jobDurationMs,
	summarizeJobResult,
} from '@/lib/card-fill-meta';
import { formatCardNumber } from '@/lib/format-card';
import { cn } from '@/lib/utils';
import { CreditCardIcon, Loader2Icon, PhoneIcon, RefreshCwIcon, RotateCcwIcon, SearchIcon } from 'lucide-react';

type CardFillDetailTarget =
	| { mode: 'card'; card: ICard; phones?: IPhone[] }
	| { mode: 'phone'; phone: IPhone };

interface CardFillDetailDialogProps {
	target: CardFillDetailTarget | null;
	jobs?: ICardFillJob[];
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

function MetaRow({ label, value }: { label: string; value: string | null | undefined }) {
	return (
		<div className='grid grid-cols-[minmax(0,7rem)_1fr] gap-x-2 gap-y-0.5 text-sm'>
			<span className='text-muted-foreground'>{label}</span>
			<span className='break-all'>{value?.trim() ? value : '—'}</span>
		</div>
	);
}

function SectionTitle({ children }: { children: string }) {
	return (
		<h3 className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>{children}</h3>
	);
}

function JobTimelineRow({ job }: { job: ICardFillJob }) {
	const duration = jobDurationMs(job);
	const summary = summarizeJobResult(job);
	const statusLabel = CARD_FILL_JOB_STATUS_LABELS[job.status] ?? job.status;
	const isFailed = job.status === 'failed';
	const isDone = job.status === 'done';

	return (
		<div
			className={cn(
				'rounded-lg border border-border bg-muted/20 p-2.5 space-y-1.5',
				isFailed && 'border-destructive/40',
				isDone && 'border-chart-2/30',
			)}
		>
			<div className='flex flex-wrap items-center justify-between gap-2'>
				<span className='text-sm font-medium'>
					{CARD_FILL_JOB_TYPE_LABELS[job.jobType] ?? job.jobType}
				</span>
				<span
					className={cn(
						'text-xs px-2 py-0.5 rounded-full border',
						isFailed && 'text-destructive border-destructive/40',
						isDone && 'text-chart-2 border-chart-2/40',
						!isFailed && !isDone && 'text-muted-foreground border-border',
					)}
				>
					{statusLabel}
				</span>
			</div>

			<div className='grid gap-0.5 text-xs text-muted-foreground'>
				<p>Создан: {formatDateTime(job.createdAt)}</p>
				{job.finishedAt ? <p>Завершён: {formatDateTime(job.finishedAt)}</p> : null}
				{duration != null ? <p className='text-foreground font-medium'>Длительность: {formatDuration(duration)}</p> : null}
			</div>

			{summary ? (
				<p className={cn('text-sm break-words', isFailed && 'text-destructive')}>{summary}</p>
			) : null}
		</div>
	);
}

function CardSnapshot({ card, phones }: { card: ICard; phones?: IPhone[] }) {
	const phoneLabel = resolveCardPhoneLabel(card, phones);
	const sourcesLabel = formatCardSources(card.sources);
	const dateSourceLabel = formatDateSource(card.dateSource);

	return (
		<section className='space-y-2 rounded-lg border border-border bg-card p-3'>
			<div className='flex items-start gap-2'>
				<CreditCardIcon size={16} className='opacity-50 shrink-0 mt-0.5' />
				<CardProviderLabel card={card} className='text-sm font-semibold' numberClassName='font-mono' />
			</div>
			<div className='grid gap-1.5 sm:grid-cols-2'>
				<MetaRow label='Маска' value={card.maskedNumber ?? undefined} />
				<MetaRow label='ФИО' value={card.owner} />
				<MetaRow label='Банк' value={card.bankName} />
				<MetaRow label='Срок' value={card.date} />
				<MetaRow label='Телефон' value={phoneLabel} />
				<MetaRow label='Источник' value={sourcesLabel} />
				<MetaRow label='Дата из' value={dateSourceLabel} />
				<MetaRow label='Обогащено' value={card.enrichedAt ? formatDateTime(card.enrichedAt) : null} />
				<MetaRow label='Баланс' value={formatCardBalanceMeta(card)} />
				<MetaRow label='Создана' value={formatDateTime(card.createdAt)} />
			</div>
		</section>
	);
}

function CardFillActions({ card, jobs }: { card: ICard; jobs?: ICardFillJob[] }) {
	const isActive = hasActiveCardJob(jobs, card.id);
	const balanceRefreshing = hasActiveBalanceFetchJob(jobs, card.id);
	const latestBalance = getLatestBalanceFetchJob(jobs, card.id);
	const balanceFailed = latestBalance?.status === 'failed';
	const showRetry = isLatestCardJobFailed(jobs, card.id);
	const statusLabel = getCardJobStatusLabel(jobs, card.id);
	const canRefreshBalance = isCardBalanceFetchReady(card);

	return (
		<section className='space-y-2'>
			<SectionTitle>Действия</SectionTitle>
			{statusLabel ? (
				<p className='text-xs text-muted-foreground'>Статус: {statusLabel}</p>
			) : null}
			{balanceFailed && latestBalance?.error ? (
				<p className='text-xs text-destructive'>Баланс: {latestBalance.error}</p>
			) : null}
			<div className='flex flex-wrap gap-1.5'>
				{canRefreshBalance ? (
					<Button
						type='button'
						variant='outline'
						size='sm'
						className='h-8 text-xs'
						disabled={balanceRefreshing}
						onClick={() => requestCardBalance(card.id, balanceFailed)}
					>
						{balanceRefreshing ? (
							<Loader2Icon size={12} className='animate-spin mr-1' />
						) : balanceFailed ? (
							<RotateCcwIcon size={12} className='mr-1' />
						) : (
							<RefreshCwIcon size={12} className='mr-1' />
						)}
						{balanceRefreshing ? 'Баланс…' : balanceFailed ? 'Повторить баланс' : 'Обновить баланс'}
					</Button>
				) : null}
				<Button
					type='button'
					variant='outline'
					size='sm'
					className='h-8 text-xs'
					disabled={isActive}
					onClick={() => requestCardEnrich(card.id, ['date'])}
				>
					Запросить дату
				</Button>
				<Button
					type='button'
					variant='outline'
					size='sm'
					className='h-8 text-xs'
					disabled={isActive}
					onClick={() => requestCardEnrich(card.id, ['pan'])}
				>
					Запросить PAN
				</Button>
				{showRetry ? (
					<Button
						type='button'
						variant='outline'
						size='sm'
						className='h-8 text-xs'
						disabled={isActive}
						onClick={() => requestCardEnrich(card.id, ['date', 'pan'], true)}
					>
						<RotateCcwIcon size={12} className='mr-1' />
						Повторить
					</Button>
				) : null}
			</div>
		</section>
	);
}

function PhoneSnapshot({ phone }: { phone: IPhone }) {
	const chips: string[] = [];
	if (phone.simSlotIndex != null) chips.push(`slot ${phone.simSlotIndex}`);
	if (phone.countryIso) chips.push(phone.countryIso.toUpperCase());
	if (phone.isDefault) chips.push('основная');

	return (
		<section className='space-y-2 rounded-lg border border-border bg-card p-3'>
			<div className='flex items-center gap-2'>
				<PhoneIcon size={16} className='opacity-50 shrink-0' />
				<span className='text-sm font-semibold'>{phone.name || 'SIM'}</span>
			</div>
			{chips.length > 0 ? (
				<div className='flex flex-wrap gap-1'>
					{chips.map(chip => (
						<span
							key={chip}
							className='inline-flex items-center rounded-md border border-border bg-background/60 px-1.5 py-0 text-[10px] text-muted-foreground'
						>
							{chip}
						</span>
					))}
				</div>
			) : null}
			<div className='grid gap-1.5'>
				<MetaRow label='Номер' value={phone.number} />
				<MetaRow label='SubId' value={phone.subId} />
			</div>
		</section>
	);
}

function PhoneLookupActions({ phone, jobs }: { phone: IPhone; jobs?: ICardFillJob[] }) {
	const latestLookup = getLatestPhoneLookupJob(jobs, phone.id);
	const lookupRunning = isJobActive(latestLookup);
	const hasLookupHistory = latestLookup != null;

	if (!phone.number?.trim()) return null;

	return (
		<section className='space-y-2'>
			<SectionTitle>Действия</SectionTitle>
			<Button
				type='button'
				variant='outline'
				size='sm'
				className='h-8 text-xs'
				disabled={lookupRunning}
				onClick={() => requestPhoneCardFill(phone.id, hasLookupHistory && !lookupRunning)}
			>
				{lookupRunning ? (
					<Loader2Icon size={13} className='animate-spin mr-1' />
				) : hasLookupHistory ? (
					<RotateCcwIcon size={13} className='mr-1' />
				) : (
					<SearchIcon size={13} className='mr-1' />
				)}
				{lookupRunning ? 'Вбив…' : hasLookupHistory ? 'Перезапросить' : 'Поиск карт'}
			</Button>
		</section>
	);
}

export default function CardFillDetailDialog({ target, jobs, open, onOpenChange }: CardFillDetailDialogProps) {
	if (!target) return null;

	const timelineJobs =
		target.mode === 'card'
			? filterJobsByCardId(jobs, target.card.id)
			: filterJobsByPhoneId(jobs, target.phone.id);

	const title =
		target.mode === 'card'
			? `Карта ${formatCardNumber(target.card)}`
			: `Номер ${target.phone.name || 'SIM'}`;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className='sm:max-w-[520px] w-full max-h-[85vh] flex flex-col overflow-hidden p-0 gap-0'>
				<DialogHeader className='px-4 pt-4 pb-2 shrink-0'>
					<DialogTitle className='text-left text-base'>{title}</DialogTitle>
				</DialogHeader>

				<div className='flex-1 min-h-0 overflow-y-auto px-4 pb-4 flex flex-col gap-3'>
					{target.mode === 'card' ? (
						<>
							<CardSnapshot card={target.card} phones={target.phones} />
							<CardFillActions card={target.card} jobs={jobs} />
						</>
					) : (
						<>
							<PhoneSnapshot phone={target.phone} />
							<PhoneLookupActions phone={target.phone} jobs={jobs} />
						</>
					)}

					<section className='space-y-2'>
						<SectionTitle>История вбива</SectionTitle>
						{timelineJobs.length > 0 ? (
							<div className='space-y-2'>
								{timelineJobs.map(job => (
									<JobTimelineRow key={job.id} job={job} />
								))}
							</div>
						) : (
							<p className='text-sm text-muted-foreground rounded-lg border border-dashed p-3'>
								Запросов вбива пока не было
							</p>
						)}
					</section>
				</div>
			</DialogContent>
		</Dialog>
	);
}
